import React, { useState } from 'react';
import { Download, Upload, RefreshCw, AlertTriangle } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { orderManager, brandSpecsManager, authManager } from '../mock';
import { useToast } from '../hooks/use-toast';

const DataManagement = () => {
  const [loading, setLoading] = useState(false);
  const { toast } = useToast();
  const currentUser = authManager.getCurrentUser();
  const isAdmin = currentUser?.role === 'Admin';

  const handleExportAllData = () => {
    try {
      const allData = {
        orders: orderManager.getAllOrders(),
        brandSpecs: brandSpecsManager.getAllBrands(),
        exportedAt: new Date().toISOString(),
        exportedBy: currentUser?.name || currentUser?.username
      };

      const dataStr = JSON.stringify(allData, null, 2);
      const dataBlob = new Blob([dataStr], { type: 'application/json' });
      
      const url = URL.createObjectURL(dataBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `ordertrack-pro-data-${new Date().toISOString().split('T')[0]}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);

      toast({
        title: 'Export Complete',
        description: 'All data has been exported successfully. You can import this file on another computer.',
      });
    } catch (error) {
      toast({
        title: 'Export Failed',
        description: error.message || 'Failed to export data.',
        variant: 'destructive'
      });
    }
  };

  const handleImportData = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setLoading(true);
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const importedData = JSON.parse(e.target.result);
        
        if (!importedData.orders || !importedData.brandSpecs) {
          throw new Error('Invalid data format. Please select a valid OrderTrack Pro export file.');
        }

        // Import brand specs first (orders depend on brands)
        const brandResult = brandSpecsManager.importBrands(importedData.brandSpecs);
        const orderResult = orderManager.importOrders(importedData.orders);

        if (brandResult.success && orderResult.success) {
          toast({
            title: 'Import Complete',
            description: `Successfully imported ${importedData.brandSpecs.length} brand specs and ${importedData.orders.length} orders.`,
          });
          
          // Refresh the page to show updated data
          setTimeout(() => {
            window.location.reload();
          }, 2000);
        } else {
          throw new Error(brandResult.message || orderResult.message);
        }
      } catch (error) {
        toast({
          title: 'Import Failed',
          description: error.message || 'Failed to import data. Please check the file format.',
          variant: 'destructive'
        });
      } finally {
        setLoading(false);
        // Reset file input
        event.target.value = '';
      }
    };

    reader.readAsText(file);
  };

  const handleExportOrders = () => {
    const result = orderManager.downloadAllOrdersJSON();
    if (result.success) {
      toast({
        title: 'Orders Exported',
        description: result.message,
      });
    } else {
      toast({
        title: 'Export Failed',
        description: result.message,
        variant: 'destructive'
      });
    }
  };

  const handleExportBrandSpecs = () => {
    const result = brandSpecsManager.downloadAllBrands();
    if (result.success) {
      toast({
        title: 'Brand Specs Exported',
        description: result.message,
      });
    } else {
      toast({
        title: 'Export Failed',
        description: result.message,
        variant: 'destructive'
      });
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-16 h-16 text-gray-400 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Access Denied</h1>
          <p className="text-gray-600 mb-6">Only administrators can access data management.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Data Management</h1>
          <p className="text-gray-600">
            Export and import data to sync between different computers
          </p>
        </div>

        {/* Export All Data */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Download className="w-5 h-5 mr-2" />
              Export All Data
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Download all your orders and brand specifications in a single file. 
              You can then import this file on another computer to sync your data.
            </p>
            <Button 
              onClick={handleExportAllData}
              className="bg-blue-600 hover:bg-blue-700"
            >
              <Download className="w-4 h-4 mr-2" />
              Export All Data
            </Button>
          </CardContent>
        </Card>

        {/* Import All Data */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="flex items-center">
              <Upload className="w-5 h-5 mr-2" />
              Import All Data
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Import data from another computer. Select the exported JSON file to restore your data.
              <span className="block text-red-600 text-sm mt-2">
                Warning: This will replace all existing data on this computer.
              </span>
            </p>
            <div className="space-y-2">
              <Label htmlFor="importFile">Select Export File</Label>
              <Input
                id="importFile"
                type="file"
                accept=".json"
                onChange={handleImportData}
                disabled={loading}
              />
            </div>
            {loading && (
              <div className="flex items-center mt-4 text-blue-600">
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                Importing data...
              </div>
            )}
          </CardContent>
        </Card>

        {/* Individual Exports */}
        <Card>
          <CardHeader>
            <CardTitle>Individual Data Exports</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-gray-600 mb-4">
              Export specific data types individually
            </p>
            <div className="space-x-4">
              <Button 
                onClick={handleExportOrders}
                variant="outline"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Orders
              </Button>
              <Button 
                onClick={handleExportBrandSpecs}
                variant="outline"
              >
                <Download className="w-4 h-4 mr-2" />
                Export Brand Specs
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Instructions */}
        <Card className="mt-6 bg-blue-50">
          <CardContent className="pt-6">
            <h3 className="font-medium text-blue-900 mb-2">How to sync data between computers:</h3>
            <ol className="text-blue-800 text-sm space-y-1 list-decimal list-inside">
              <li>On Computer 1 (where you have data): Click "Export All Data"</li>
              <li>Transfer the downloaded JSON file to Computer 2 (via email, USB, etc.)</li>
              <li>On Computer 2: Go to Data Management → Import All Data</li>
              <li>Select the JSON file and import</li>
              <li>Your data will now be synced on both computers</li>
            </ol>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default DataManagement;